import React, { useState, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { handleAddModifyOperation } from '../../../Api/ApiMethods';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { normalizeFieldValue, displayDecimal } from '../../../_helper/regexHandler';
import { useModalCrudState } from '../../../_helper/hooks/useModalCrudState';

const PatchCallFormWorkInfoModal = ({ isOpen, toggle, callFormId, initialData, refreshData }) => {
    const initialFormData = useMemo(() => ({
        documentType: 'work',
        bonusEligibility: null,
    }), []);

    const {
        formData,
        setFormData,
        handleCancel: handleCrudCancel,
    } = useModalCrudState({
        isOpen,
        mode: initialData ? 'modify' : 'add',
        selectedItem: initialData || null,
        initialData: initialFormData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => ({
            ...initialData,
            documentType: selectedItem?.isTrialDocument === true ? 'trial' : 'work',
            bonusEligibility: selectedItem?.bonusEligibility !== undefined ? selectedItem.bonusEligibility : null,
        }),
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleRadioChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "bonusEligibility") {
            const processedValue = normalizeFieldValue(name, value);
            setFormData(prev => ({ ...prev, [name]: processedValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        if (!callFormId) {
            toast.error("Güncellenecek çağrı formu ID'si bulunamadı.");
            return;
        }
        setIsSaving(true);

        const amount = formData.bonusEligibility === '' || formData.bonusEligibility === null
            ? null
            : parseFloat(formData.bonusEligibility);

        if (formData.bonusEligibility !== '' && formData.bonusEligibility !== null && isNaN(amount)) {
            toast.error("Prim hakediş tutarı geçerli bir sayı olmalıdır.");
            setIsSaving(false);
            return;
        }

        const payload = {
            isTrialDocument: formData.documentType === 'trial',
            isWorkDocument: formData.documentType === 'work',
            bonusEligibility: amount,
        };

        if (payload.isTrialDocument) {
            payload.isWorkDocument = false;
        } else {
            payload.isWorkDocument = true;
        }
        
        const apiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/callform/patch-callform`;

        try {
            await handleAddModifyOperation({
                apiEndpoint: apiEndpoint,
                selectedItem: { id: callFormId },
                mode: 'modify',
                data: payload,
                refreshDataCallback: () => {
                    if (refreshData) refreshData();
                    toast.success('Dosya bilgileri başarıyla güncellendi.');
                },
                componentName: 'Dosya Bilgisi',
            });
            handleCancel();
        } catch {
            // Hata toast'ı handleAddModifyOperation tarafından basılır
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        handleCrudCancel();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>Dosya Bilgilerini Güncelle</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup tag="fieldset" className="mb-3">
                        <Label>Dosya Tipi</Label>
                        <div className="d-flex mt-2">
                            <FormGroup check className="radio radio-primary me-3">
                                <Input
                                    id="documentTypeWork"
                                    name="documentType"
                                    type="radio"
                                    value="work"
                                    checked={formData.documentType === 'work'}
                                    onChange={handleRadioChange}
                                />
                                <Label check for="documentTypeWork" className="ms-1 mb-0">İş Dosyası</Label>
                            </FormGroup>
                            <FormGroup check className="radio radio-primary">
                                <Input
                                    id="documentTypeTrial"
                                    name="documentType"
                                    type="radio"
                                    value="trial"
                                    checked={formData.documentType === 'trial'}
                                    onChange={handleRadioChange}
                                />
                                <Label check for="documentTypeTrial" className="ms-1 mb-0">Deneme Dosyası</Label>
                            </FormGroup>
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <Label for="bonusEligibility">Prim Hakediş Tutarı:</Label>
                        <Input
                            type="text"
                            id="bonusEligibility"
                            name="bonusEligibility"
                            value={displayDecimal(formData.bonusEligibility === null ? '' : formData.bonusEligibility)}
                            onChange={handleInputChange}
                            placeholder="Örn: 1000,50"
                        />
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
                <Button color="secondary" onClick={handleCancel} className="ms-2" disabled={isSaving}>
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

PatchCallFormWorkInfoModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    callFormId: PropTypes.string,
    initialData: PropTypes.shape({
        isTrialDocument: PropTypes.bool,
        bonusEligibility: PropTypes.number,
    }),
    refreshData: PropTypes.func,
};

export default PatchCallFormWorkInfoModal;
